import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import axios from "axios";
import {
  Shield, ShieldCheck, Users, Building2, CreditCard, Trash2, CheckCircle, XCircle, Clock,
  AlertTriangle, DollarSign, RefreshCw, Eye, Pencil, FileClock, LogOut,
  Search, ChevronLeft, ChevronRight, ArrowUpDown, TrendingUp, TrendingDown,
  Sparkles, Megaphone, MailWarning, Download, Loader2,
} from "lucide-react";
import { format, formatDistanceToNow } from "date-fns";
import BandDetailDrawer from "../components/system-admin/BandDetailDrawer";
import Sparkline from "../components/system-admin/Sparkline";
import { BACKEND_URL, API } from "../lib/api";

const API_BASE = BACKEND_URL;
const TOKEN_KEY = "systemAdminToken";

// ---- Auth-aware axios instance ----
const systemApi = axios.create({ baseURL: API });
systemApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const PW_RULES = [
  { label: "At least 12 characters", test: (v) => v.length >= 12 },
  { label: "One uppercase letter", test: (v) => /[A-Z]/.test(v) },
  { label: "One number", test: (v) => /\d/.test(v) },
  { label: "One special character", test: (v) => /[^A-Za-z0-9]/.test(v) },
];

const SystemAdmin = () => {
  const [token, setToken] = useState(localStorage.getItem(TOKEN_KEY) || "");
  const [authChecked, setAuthChecked] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [lockoutMessage, setLockoutMessage] = useState("");

  const [stats, setStats] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [bands, setBands] = useState([]);
  // iter-101: server-side query state
  const [searchQ, setSearchQ] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [attention, setAttention] = useState(null);
  const [attentionCounts, setAttentionCounts] = useState({});
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [bandsLoading, setBandsLoading] = useState(false);
  const [bulkDigestLoading, setBulkDigestLoading] = useState(false);
  // iter-106 — system health
  const [health, setHealth] = useState(null);
  const [recentErrors, setRecentErrors] = useState(null);
  const [errorsLoading, setErrorsLoading] = useState(false);
  const [showHealthDetail, setShowHealthDetail] = useState(false);
  // iter-105 — bulk digest progress badge
  const [activeJob, setActiveJob] = useState(null);
  const jobPollRef = useRef(null);

  const [auditOpen, setAuditOpen] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerBandId, setDrawerBandId] = useState(null);
  // iter-101b: orphan-memberships housekeeping
  const [orphanCount, setOrphanCount] = useState(null);
  const [orphanLoading, setOrphanLoading] = useState(false);
  // iter-106: bulk align role conflicts
  const [roleAlignLoading, setRoleAlignLoading] = useState(false);
  // iter-113: bulk clean orphan attendance (non-member rows on upcoming events)
  const [orphanAttLoading, setOrphanAttLoading] = useState(false);
  // iter-103: broadcast composer
  const [broadcastOpen, setBroadcastOpen] = useState(false);
  const [broadcastSubject, setBroadcastSubject] = useState("");
  const [broadcastBody, setBroadcastBody] = useState("");
  const [broadcastTestOnly, setBroadcastTestOnly] = useState(true);
  const [broadcastSending, setBroadcastSending] = useState(false);
  const [broadcastHistory, setBroadcastHistory] = useState([]);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedBand, setSelectedBand] = useState(null);
  const [editForm, setEditForm] = useState({
    subscription_status: "trial",
    subscription_plan: "monthly",
    subscription_end: "",
    notes: "",
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [bandToDelete, setBandToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");

  // ---- Bootstrap: validate token on load ----
  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setIsAuthenticated(false);
    setStats(null);
    setBands([]);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) { setAuthChecked(true); return; }
      try {
        await systemApi.get("/system/auth/me");
        if (!cancelled) setIsAuthenticated(true);
      } catch {
        if (!cancelled) logout();
      } finally {
        if (!cancelled) setAuthChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, [token, logout]);

  // Auto-logout on any 401/expired
  useEffect(() => {
    const id = systemApi.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401 && localStorage.getItem(TOKEN_KEY)) {
          toast.error("Session expired — please log in again");
          logout();
        }
        return Promise.reject(err);
      },
    );
    return () => systemApi.interceptors.response.eject(id);
  }, [logout]);

  const fetchStats = useCallback(async () => {
    try {
      const s = await systemApi.get("/system/stats");
      setStats(s.data);
    } catch { /* interceptor handles 401 */ }
    try {
      const m = await systemApi.get("/system/metrics");
      setMetrics(m.data);
    } catch { /* silent */ }
    try {
      const o = await systemApi.get("/system/maintenance/orphan-memberships");
      setOrphanCount(o.data?.count ?? null);
    } catch { /* silent */ }
    // iter-106 — health snapshot
    try {
      const h = await systemApi.get("/system/health");
      setHealth(h.data);
    } catch { /* silent */ }
  }, []);

  const repairOrphans = async () => {
    if (!window.confirm(`Repair ${orphanCount} user${orphanCount === 1 ? "" : "s"} with broken memberships? This rebuilds their band memberships from their legacy band_id.`)) return;
    setOrphanLoading(true);
    try {
      const r = await systemApi.post("/system/maintenance/repair-orphan-memberships");
      toast.success(`Repaired ${r.data.repaired_count} user${r.data.repaired_count === 1 ? "" : "s"}`);
      setOrphanCount(0);
    } catch {
      toast.error("Repair failed — check audit log");
    } finally {
      setOrphanLoading(false);
    }
  };

  // iter-106 — sweep all role conflicts (admin in one place, player in
  // another) in one shot. Safe to run repeatedly; reports scanned vs fixed.
  const alignAllRoles = async () => {
    if (!window.confirm("Sweep every user and align their legacy role with their per-band memberships? Admin always wins — no demotions. Safe to run anytime.")) return;
    setRoleAlignLoading(true);
    try {
      const r = await systemApi.post("/system/users/align-all-roles");
      const { scanned = 0, fixed = 0 } = r.data || {};
      toast.success(`Scanned ${scanned} user${scanned === 1 ? "" : "s"}; fixed ${fixed} role conflict${fixed === 1 ? "" : "s"}.`);
      // Refresh the bands view so any drawer-level counts/users update.
      fetchBands();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Role align sweep failed");
    } finally {
      setRoleAlignLoading(false);
    }
  };

  // iter-113 — sweep orphan attendance: removes attendance rows on
  // UPCOMING events for any player whose player_type is dep / inactive /
  // info_only. Idempotent — safe to re-run.
  const cleanOrphanAttendance = async () => {
    if (!window.confirm("Sweep all attendance rows on upcoming events for non-member players (dep / inactive / info_only)? Safe and idempotent.")) return;
    setOrphanAttLoading(true);
    try {
      const r = await systemApi.post("/system/maintenance/clean-non-member-attendance");
      const { orphan_rows_found = 0, rows_cleaned = 0, players_touched = 0 } = r.data || {};
      toast.success(
        `Found ${orphan_rows_found} orphan row${orphan_rows_found === 1 ? "" : "s"}; cleaned ${rows_cleaned} across ${players_touched} player${players_touched === 1 ? "" : "s"}.`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Orphan attendance sweep failed");
    } finally {
      setOrphanAttLoading(false);
    }
  };


  const fetchBands = useCallback(async () => {
    setBandsLoading(true);
    try {
      const params = { page, page_size: pageSize, sort, order };
      if (searchQ.trim()) params.q = searchQ.trim();
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      if (attention) params.attention = attention;
      const r = await systemApi.get("/system/bands", { params });
      setBands(r.data.bands || []);
      setTotal(r.data.total || 0);
      setTotalPages(r.data.total_pages || 1);
      setAttentionCounts(r.data.attention_counts || {});
    } catch {
      /* interceptor handles 401 */
    } finally {
      setBandsLoading(false);
    }
  }, [page, pageSize, sort, order, searchQ, statusFilter, attention]);

  const fetchAll = useCallback(() => {
    fetchStats();
    fetchBands();
  }, [fetchStats, fetchBands]);

  useEffect(() => {
    if (isAuthenticated) fetchStats();
  }, [isAuthenticated, fetchStats]);

  useEffect(() => {
    if (isAuthenticated) fetchBands();
  }, [isAuthenticated, fetchBands]);

  // Debounce search input → searchQ
  useEffect(() => {
    const id = setTimeout(() => {
      setSearchQ(searchInput);
      setPage(1);
    }, 350);
    return () => clearTimeout(id);
  }, [searchInput]);

  const authenticate = async () => {
    setLockoutMessage("");
    if (!loginEmail || !loginPassword) {
      toast.error("Email and password are required");
      return;
    }
    setLoginLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/api/system/auth/login`, {
        email: loginEmail,
        password: loginPassword,
      });
      const t = res.data.token;
      localStorage.setItem(TOKEN_KEY, t);
      setToken(t);
      setIsAuthenticated(true);
      setLoginPassword("");
      toast.success("Signed in");
    } catch (err) {
      if (err?.response?.status === 429) {
        setLockoutMessage(err.response.data?.detail || "Too many failed attempts. Try again later.");
      } else {
        toast.error(err?.response?.data?.detail || "Invalid email or password");
      }
    } finally {
      setLoginLoading(false);
    }
  };

  const openAudit = async () => {
    setAuditOpen(true);
    setAuditLoading(true);
    try {
      const r = await systemApi.get("/system/audit");
      setAuditEntries(r.data.entries || []);
    } catch {
      toast.error("Could not load audit log");
    } finally {
      setAuditLoading(false);
    }
  };

  const handleMarkPaid = async (bandId) => {
    try {
      await systemApi.post(`/system/bands/${bandId}/mark-paid`);
      toast.success("Subscription marked as paid for 1 year");
      fetchAll();
    } catch {
      toast.error("Failed to update subscription");
    }
  };

  // iter-119: extend a band's trial by N days (or set them to active).
  // Used to undo accidental lockouts + for preview/staging testing.
  const handleExtendTrial = async (bandId, bandName) => {
    const days = window.prompt(
      `Extend ${bandName}'s trial by how many days?\n\n(Type a positive number, or "active" to mark them paid-active.)`,
      "30"
    );
    if (days === null) return;
    const trimmed = String(days).trim();
    try {
      let resp;
      if (trimmed.toLowerCase() === "active") {
        resp = await systemApi.post(`/system/bands/${bandId}/extend-trial`, { action: "set-active" });
      } else {
        const n = parseInt(trimmed, 10);
        if (!n || n <= 0 || n > 365) { toast.error("Days must be 1-365"); return; }
        resp = await systemApi.post(`/system/bands/${bandId}/extend-trial`, { days: n });
      }
      toast.success(resp.data?.message || "Trial updated");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to extend trial");
    }
  };

  const openDrawer = (band) => {
    setDrawerBandId(band.id);
    setDrawerOpen(true);
  };

  const toggleSort = (field) => {
    if (sort === field) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setSort(field);
      setOrder("desc");
    }
    setPage(1);
  };

  const toggleAttention = (key) => {
    setAttention((cur) => (cur === key ? null : key));
    setPage(1);
  };

  // iter-104c — bulk send digest to all stale bands (background task)
  const sendDigestToAllStale = async () => {
    const count = attentionCounts.digest_stale || 0;
    if (count === 0) {
      toast.info("No stale-digest bands to process");
      return;
    }
    if (!window.confirm(`Force-send the daily digest to ${count} band${count === 1 ? "" : "s"} with stale digests? Each band's admins will receive an email. This runs in the background — emails may take a few minutes.`)) return;
    setBulkDigestLoading(true);
    try {
      const r = await systemApi.post("/system/daily-digest/send-all-stale?limit=200");
      const queued = r.data?.bands_queued || 0;
      const matching = r.data?.bands_matching || 0;
      const hasMore = r.data?.has_more;
      toast.success(
        `Queued ${queued} band${queued === 1 ? "" : "s"}${hasMore ? ` (of ${matching} stale — re-run to process the rest)` : ""}. Emails sending in the background.`
      );
      // Refresh after a short delay so admins see counts start dropping.
      setTimeout(() => { fetchBands(); }, 2500);
      // iter-105: start polling the job badge.
      pollActiveJob();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk send failed");
    } finally {
      setBulkDigestLoading(false);
    }
  };

  // iter-105 — bands export (CSV / XLSX). Honours active filters.
  const exportBands = async (format) => {
    const params = new URLSearchParams();
    if (searchQ) params.set("q", searchQ);
    if (statusFilter && statusFilter !== "all") params.set("status", statusFilter);
    if (attention) params.set("attention", attention);
    if (sort) params.set("sort", sort);
    if (order) params.set("order", order);
    try {
      const url = `/system/bands/export.${format}?${params.toString()}`;
      const r = await systemApi.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], {
        type: format === "csv"
          ? "text/csv"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const dlUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = dlUrl;
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);
      a.download = `vivo_bands_${ts}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(dlUrl);
      toast.success(`Bands exported as ${format.toUpperCase()}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${format.toUpperCase()} export failed`);
    }
  };

  // iter-105 — bulk-digest progress polling. Starts after a bulk send is
  // triggered (or on mount if a job is already in flight); stops when the
  // job finishes.
  const pollActiveJob = useCallback(async () => {
    try {
      const r = await systemApi.get("/system/jobs/active?type=daily_digest_send_all_stale");
      const job = r.data?.job;
      setActiveJob(job || null);
      if (jobPollRef.current) {
        clearTimeout(jobPollRef.current);
        jobPollRef.current = null;
      }
      if (job && !job.finished_at) {
        jobPollRef.current = setTimeout(pollActiveJob, 3000);
      } else if (job && job.finished_at) {
        // Keep "done" pill on screen for a few seconds, then clear.
        jobPollRef.current = setTimeout(() => setActiveJob(null), 5000);
      }
    } catch {
      /* polling is best-effort */
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    pollActiveJob();
    return () => {
      if (jobPollRef.current) clearTimeout(jobPollRef.current);
    };
  }, [isAuthenticated, pollActiveJob]);

  // iter-103 — broadcast
  const openBroadcast = async () => {
    setBroadcastOpen(true);
    setBroadcastSubject("");
    setBroadcastBody("");
    setBroadcastTestOnly(true);
    try {
      const r = await systemApi.get("/system/broadcasts?limit=20");
      setBroadcastHistory(r.data.broadcasts || []);
    } catch { /* silent */ }
  };

  const sendBroadcast = async () => {
    if (!broadcastSubject.trim() || !broadcastBody.trim()) {
      toast.error("Subject and body are required");
      return;
    }
    if (!broadcastTestOnly) {
      if (!window.confirm("This will email EVERY band admin. Continue?")) return;
    }
    setBroadcastSending(true);
    try {
      const r = await systemApi.post("/system/broadcasts", {
        subject: broadcastSubject,
        body_html: broadcastBody,
        test_only: broadcastTestOnly,
      });
      toast.success(`Sent to ${r.data.sent_count}/${r.data.recipient_count} (${r.data.failed_count} failed)`);
      // Refresh history
      const h = await systemApi.get("/system/broadcasts?limit=20");
      setBroadcastHistory(h.data.broadcasts || []);
      if (!broadcastTestOnly) setBroadcastOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Broadcast failed");
    } finally {
      setBroadcastSending(false);
    }
  };

  const handleEditSubscription = (band) => {
    setSelectedBand(band);
    setEditForm({
      subscription_status: band.subscription_status || "trial",
      subscription_plan: band.subscription_plan || "monthly",
      subscription_end: band.subscription_end ? band.subscription_end.split("T")[0] : "",
      notes: band.admin_notes || "",
    });
    setEditDialogOpen(true);
  };

  const handleSaveSubscription = async () => {
    try {
      await systemApi.put(`/system/bands/${selectedBand.id}/subscription`, editForm);
      toast.success("Subscription updated");
      setEditDialogOpen(false);
      fetchAll();
    } catch {
      toast.error("Failed to update subscription");
    }
  };

  const openDelete = (band) => {
    setBandToDelete(band);
    setDeleteConfirmText("");
    setDeleteDialogOpen(true);
  };

  const handleDeleteBand = async () => {
    if (!bandToDelete) return;
    if (deleteConfirmText.trim().toLowerCase() !== bandToDelete.name.trim().toLowerCase()) {
      toast.error("Type the band name exactly to confirm");
      return;
    }
    try {
      await systemApi.post(`/system/bands/${bandToDelete.id}/delete`, {
        confirm_name: deleteConfirmText,
      });
      toast.success(`Band "${bandToDelete.name}" deleted`);
      setDeleteDialogOpen(false);
      setBandToDelete(null);
      setDeleteConfirmText("");
      fetchAll();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete band");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-500/20 text-green-400"><CheckCircle className="w-3 h-3 mr-1" />Active</Badge>;
      case "trial":
        return <Badge className="bg-blue-500/20 text-blue-400"><Clock className="w-3 h-3 mr-1" />Trial</Badge>;
      case "expired":
        return <Badge className="bg-red-500/20 text-red-400"><XCircle className="w-3 h-3 mr-1" />Expired</Badge>;
      case "cancelled":
        return <Badge className="bg-orange-500/20 text-orange-400"><AlertTriangle className="w-3 h-3 mr-1" />Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const fmtAuditTime = (iso) => {
    if (!iso) return "—";
    try { return format(new Date(iso), "MMM d HH:mm:ss"); } catch { return iso; }
  };

  // ---- Login screen ----
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4 text-muted-foreground">
        Loading…
      </div>
    );
  }

  if (!isAuthenticated) {
    const allRulesPass = PW_RULES.every((r) => r.test(loginPassword));
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md glass-card">
          <CardHeader className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
              <Shield className="w-8 h-8 text-primary" />
            </div>
            <CardTitle className="text-2xl font-heading">System Admin</CardTitle>
            <p className="text-muted-foreground mt-2">Sign in to the management console</p>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="you@support.vivomusicianmanagement.co.uk"
                autoComplete="email"
                data-testid="system-admin-email"
                onKeyDown={(e) => e.key === "Enter" && authenticate()}
              />
            </div>
            <div className="space-y-2">
              <Label>Password</Label>
              <Input
                type="password"
                value={loginPassword}
                onChange={(e) => setLoginPassword(e.target.value)}
                placeholder="••••••••••••"
                autoComplete="current-password"
                data-testid="system-admin-password"
                onKeyDown={(e) => e.key === "Enter" && authenticate()}
              />
              {loginPassword && !allRulesPass && (
                <ul className="text-xs text-muted-foreground mt-1 space-y-0.5">
                  {PW_RULES.map((r) => (
                    <li key={r.label} className={r.test(loginPassword) ? "text-emerald-400" : "text-muted-foreground/70"}>
                      {r.test(loginPassword) ? "✓ " : "○ "}{r.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            {lockoutMessage && (
              <div
                className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-md p-3"
                data-testid="system-admin-lockout-msg"
              >
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                {lockoutMessage}
                <div className="text-xs opacity-80 mt-1">
                  An alert email has been sent to the support address with the IP details.
                </div>
              </div>
            )}
            <Button
              className="w-full"
              onClick={authenticate}
              disabled={loginLoading || !!lockoutMessage}
              data-testid="system-admin-signin-btn"
            >
              {loginLoading ? "Signing in…" : "Sign In"}
            </Button>
            <p className="text-xs text-muted-foreground text-center">
              5 failed attempts in 15 min trigger an automatic lockout + email alert.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ---- Authenticated console ----
  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-3xl font-heading font-bold flex items-center gap-3">
              <Shield className="w-8 h-8 text-primary" />
              System Admin Console
            </h1>
            <p className="text-muted-foreground mt-1">Manage all bands and subscriptions</p>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            {/* iter-105 — bulk digest progress badge */}
            {activeJob && (
              <div
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-xs border ${
                  activeJob.finished_at
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                    : "border-amber-500/40 bg-amber-500/10 text-amber-300"
                }`}
                data-testid="bulk-digest-progress"
                title={activeJob.finished_at
                  ? `Finished ${new Date(activeJob.finished_at).toLocaleTimeString()}`
                  : "Background digest send in progress"}
              >
                {activeJob.finished_at ? (
                  <CheckCircle className="w-3.5 h-3.5" />
                ) : (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                )}
                <span>
                  Bulk digest: {activeJob.processed || 0}/{activeJob.total || 0}
                  {activeJob.emails_sent > 0 && ` · ${activeJob.emails_sent} email${activeJob.emails_sent === 1 ? "" : "s"} sent`}
                  {activeJob.finished_at ? " · done" : ""}
                </span>
              </div>
            )}
            <Button variant="outline" onClick={openBroadcast} data-testid="system-admin-broadcast-btn">
              <Megaphone className="w-4 h-4 mr-2" /> Broadcast
            </Button>
            <Button
              variant="outline"
              onClick={() => window.location.assign("/system-admin/marketing")}
              data-testid="system-admin-fb-marketing-btn"
            >
              <Megaphone className="w-4 h-4 mr-2" /> FB Marketing
            </Button>
            <Button variant="outline" onClick={openAudit} data-testid="system-admin-audit-btn">
              <FileClock className="w-4 h-4 mr-2" /> Audit log
            </Button>
            <Button variant="outline" onClick={fetchAll}>
              <RefreshCw className="w-4 h-4 mr-2" /> Refresh
            </Button>
            <Button variant="outline" onClick={logout} data-testid="system-admin-logout-btn">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </div>

        {/* Stats — iter-102: MRR + Churn + Growth + classic counters */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {/* MRR */}
            <Card className="glass-card lg:col-span-2"><CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground uppercase tracking-wide">MRR (£)</span>
                <DollarSign className="w-4 h-4 text-yellow-400" />
              </div>
              <div className="flex items-end justify-between gap-2">
                <p className="text-3xl font-bold leading-none">£{(metrics?.mrr_gbp ?? 0).toFixed(2)}</p>
                {metrics?.daily_signups && (
                  <Sparkline
                    data={metrics.daily_signups.map((d) => d.count)}
                    width={110} height={28}
                    strokeColor="#22d3ee"
                    fillColor="#22d3ee"
                    testid="mrr-sparkline"
                  />
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                {metrics?.mrr_breakdown?.monthly_count || 0} monthly · {metrics?.mrr_breakdown?.yearly_count || 0} yearly
              </p>
            </CardContent></Card>

            {/* Churn */}
            <Card className="glass-card"><CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground uppercase tracking-wide">Churn 30d</span>
                {(metrics?.churn_30d_pct || 0) >= 5
                  ? <TrendingDown className="w-4 h-4 text-red-400" />
                  : <TrendingUp className="w-4 h-4 text-emerald-400" />}
              </div>
              <p className={`text-3xl font-bold leading-none ${(metrics?.churn_30d_pct || 0) >= 5 ? "text-red-400" : "text-emerald-400"}`}>
                {metrics?.churn_30d_pct ?? 0}%
              </p>
              <p className="text-xs text-muted-foreground mt-1">{metrics?.churned_30d || 0} bands churned</p>
            </CardContent></Card>

            {/* Growth */}
            <Card className="glass-card"><CardContent className="p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs text-muted-foreground uppercase tracking-wide">New 30d</span>
                <Sparkles className="w-4 h-4 text-blue-400" />
              </div>
              <p className="text-3xl font-bold leading-none text-blue-300">{metrics?.growth_30d ?? 0}</p>
              <p className="text-xs text-muted-foreground mt-1">{metrics?.new_today ?? 0} today</p>
            </CardContent></Card>

            {/* Active / Trial */}
            <Card className="glass-card"><CardContent className="p-4 text-center">
              <CheckCircle className="w-6 h-6 mx-auto text-green-400 mb-1" />
              <p className="text-2xl font-bold">{stats.bands.paid}</p>
              <p className="text-xs text-muted-foreground">Paid</p>
            </CardContent></Card>
            <Card className="glass-card"><CardContent className="p-4 text-center">
              <Clock className="w-6 h-6 mx-auto text-blue-400 mb-1" />
              <p className="text-2xl font-bold">{stats.bands.trial}</p>
              <p className="text-xs text-muted-foreground">On Trial</p>
            </CardContent></Card>
          </div>
        )}


        {/* iter-106 — System health card */}
        {health && (
          <Card className="glass-card" data-testid="system-health-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Shield className="w-4 h-4 text-primary" /> System health
                </CardTitle>
                <div className="flex items-center gap-2">
                  <HealthPill
                    ok={health.mongo?.ok}
                    label={health.mongo?.ok ? `Mongo · ${health.mongo?.ping_ms}ms` : "Mongo down"}
                    testid="health-mongo"
                  />
                  <HealthPill
                    ok={(health.scheduler?.active_locks_2h || 0) > 0}
                    warnIfNotOk
                    label={`Scheduler · ${health.scheduler?.active_locks_2h || 0} locks (2h)`}
                    testid="health-scheduler"
                  />
                  <HealthPill
                    ok={(health.errors?.last_hour || 0) === 0}
                    warnIfNotOk
                    label={`500s · ${health.errors?.last_hour || 0} (1h) · ${health.errors?.last_24h || 0} (24h)`}
                    testid="health-errors"
                  />
                  <Button
                    variant="outline" size="sm"
                    onClick={async () => {
                      setShowHealthDetail((v) => !v);
                      if (recentErrors === null && !showHealthDetail) {
                        setErrorsLoading(true);
                        try {
                          const r = await systemApi.get("/system/errors/recent?limit=20");
                          setRecentErrors(r.data?.errors || []);
                        } catch { setRecentErrors([]); }
                        setErrorsLoading(false);
                      }
                    }}
                    data-testid="toggle-health-detail"
                  >
                    {showHealthDetail ? "Hide details" : "Details"}
                  </Button>
                </div>
              </div>
            </CardHeader>
            {showHealthDetail && (
              <CardContent className="pt-0 space-y-3">
                {/* Scheduler */}
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Latest scheduler lock</div>
                  {health.scheduler?.latest_lock ? (
                    <div className="text-xs font-mono bg-background/40 rounded p-2 border border-white/5 break-all">
                      <div><strong>Job:</strong> {health.scheduler.latest_lock.job_name || health.scheduler.latest_lock.id || "—"}</div>
                      <div><strong>Created:</strong> {health.scheduler.latest_lock.created_at || "—"}</div>
                      {health.scheduler.latest_lock.locked_by && (
                        <div><strong>Locked by:</strong> {health.scheduler.latest_lock.locked_by}</div>
                      )}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground italic">No scheduler locks recorded yet.</p>
                  )}
                </div>
                {/* Recent errors */}
                <div>
                  <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1 flex items-center justify-between">
                    <span>Recent 500s (last 20)</span>
                    <Button
                      variant="ghost" size="sm"
                      className="h-6 text-xs"
                      onClick={async () => {
                        setErrorsLoading(true);
                        try {
                          const r = await systemApi.get("/system/errors/recent?limit=20");
                          setRecentErrors(r.data?.errors || []);
                        } catch { /* silent */ }
                        setErrorsLoading(false);
                      }}
                    >
                      <RefreshCw className="w-3 h-3" />
                    </Button>
                  </div>
                  {errorsLoading && <p className="text-xs text-muted-foreground">Loading…</p>}
                  {!errorsLoading && Array.isArray(recentErrors) && recentErrors.length === 0 && (
                    <p className="text-xs text-emerald-300 italic">No unhandled 500s recorded. 🎉</p>
                  )}
                  {!errorsLoading && Array.isArray(recentErrors) && recentErrors.length > 0 && (
                    <div className="text-xs space-y-1 max-h-64 overflow-y-auto" data-testid="recent-errors-list">
                      {recentErrors.map((e) => (
                        <div key={e.id} className="font-mono bg-background/40 rounded p-2 border border-red-500/20" data-testid={`error-${e.id}`}>
                          <div className="flex items-center justify-between gap-2 mb-0.5">
                            <span className="text-red-300">{e.exception_type}</span>
                            <span className="text-muted-foreground">{e.created_at?.slice(0, 19).replace("T", " ")}</span>
                          </div>
                          <div className="text-foreground"><strong>{e.method}</strong> {e.path}</div>
                          <div className="text-muted-foreground truncate" title={e.message}>{e.message}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        )}


        {/* iter-106 — Bulk role-conflict sweep. Always shown for system
            admins because conflicts can re-appear if external tools touch
            user docs; this is the one-click broom. */}
        <Card className="glass-card" data-testid="role-align-card">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">
                  Role consistency sweep
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Aligns every user&apos;s legacy <code>users.role</code> with their per-band <code>memberships[band_id].role</code>. Admin wins ties (no one is demoted). Use after a legacy promotion didn&apos;t reflect in the sidebar.
                </p>
              </div>
            </div>
            <Button
              onClick={alignAllRoles}
              variant="outline"
              disabled={roleAlignLoading}
              data-testid="align-all-roles-btn"
            >
              <ShieldCheck className="w-4 h-4 mr-2" />
              {roleAlignLoading ? "Sweeping…" : "Fix all role conflicts"}
            </Button>
          </CardContent>
        </Card>

        {/* iter-113 — Orphan attendance sweep. Removes attendance rows on
            UPCOMING events for non-member players (dep / inactive /
            info_only). Stops deps continuing to receive reminder emails
            after a Member→Dep downgrade. Idempotent. */}
        <Card className="glass-card" data-testid="orphan-attendance-card">
          <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-start gap-3">
              <Trash2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">
                  Clean orphan attendance
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  Removes attendance rows on <strong>upcoming</strong> events for any player whose <code>player_type</code> is <code>dep</code>, <code>inactive</code>, or <code>info_only</code>. Use after a Member→Dep downgrade if a player is still receiving event reminders. Safe and idempotent.
                </p>
              </div>
            </div>
            <Button
              onClick={cleanOrphanAttendance}
              variant="outline"
              disabled={orphanAttLoading}
              data-testid="clean-orphan-attendance-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {orphanAttLoading ? "Sweeping…" : "Clean orphan attendance"}
            </Button>
          </CardContent>
        </Card>

        {/* Orphan memberships banner — iter-101b */}
        {orphanCount > 0 && (
          <Card className="glass-card border-orange-500/40 bg-orange-500/5" data-testid="orphan-memberships-banner">
            <CardContent className="p-4 flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium text-orange-200">
                    {orphanCount} user{orphanCount === 1 ? "" : "s"} need membership repair
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    These users have a legacy <code>band_id</code> but an empty <code>memberships</code> array — so admin toggles and per-band permissions don&apos;t resolve. One click rebuilds them from their existing band_id + role.
                  </p>
                </div>
              </div>
              <Button
                onClick={repairOrphans}
                disabled={orphanLoading}
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="repair-orphan-memberships-btn"
              >
                {orphanLoading ? "Repairing…" : `Repair ${orphanCount} user${orphanCount === 1 ? "" : "s"}`}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Filters + search */}
        <Card className="glass-card">
          <CardContent className="p-4 space-y-3">
            {/* Search + status row */}
            <div className="flex flex-wrap items-center gap-3">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by band name…"
                  className="pl-9"
                  data-testid="system-admin-search"
                />
              </div>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[160px] lg:hidden"><SelectValue placeholder="All statuses" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
              {/* Desktop status chips */}
              <div className="hidden lg:flex items-center gap-1.5" data-testid="status-chips">
                {[
                  { value: "all", label: "All", icon: null },
                  { value: "trial", label: "Trial", icon: Clock },
                  { value: "active", label: "Active", icon: CheckCircle },
                  { value: "expired", label: "Expired", icon: XCircle },
                  { value: "cancelled", label: "Cancelled", icon: AlertTriangle },
                ].map((opt) => {
                  const active = statusFilter === opt.value;
                  const Icon = opt.icon;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => { setStatusFilter(opt.value); setPage(1); }}
                      data-testid={`status-chip-${opt.value}`}
                      className={`inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-full border transition-colors ${
                        active
                          ? "bg-primary/20 border-primary/50 text-primary"
                          : "bg-background/40 border-white/10 text-muted-foreground hover:border-white/30"
                      }`}
                    >
                      {Icon && <Icon className="w-3 h-3" />}
                      {opt.label}
                    </button>
                  );
                })}
              </div>
              <div className="text-sm text-muted-foreground">
                {bandsLoading ? "Loading…" : `${total.toLocaleString()} band${total === 1 ? "" : "s"}`}
              </div>
            </div>
            {/* Smart filter chips */}
            <div className="flex flex-wrap gap-2">
              <span className="text-xs text-muted-foreground self-center mr-1">Needs attention:</span>
              <FilterChip active={attention === "trial_ending_7d"} onClick={() => toggleAttention("trial_ending_7d")} count={attentionCounts.trial_ending_7d} testid="chip-trial-ending">
                <Clock className="w-3 h-3" /> Trial ending in 7d
              </FilterChip>
              <FilterChip active={attention === "expired"} onClick={() => toggleAttention("expired")} count={attentionCounts.expired} testid="chip-expired">
                <XCircle className="w-3 h-3" /> Expired
              </FilterChip>
              <FilterChip active={attention === "no_admin"} onClick={() => toggleAttention("no_admin")} count={attentionCounts.no_admin} testid="chip-no-admin">
                <AlertTriangle className="w-3 h-3" /> No admin
              </FilterChip>
              <FilterChip active={attention === "no_events_30d"} onClick={() => toggleAttention("no_events_30d")} count={attentionCounts.no_events_30d} testid="chip-no-events">
                <AlertTriangle className="w-3 h-3" /> No events 30d
              </FilterChip>
              <FilterChip active={attention === "digest_stale"} onClick={() => toggleAttention("digest_stale")} count={attentionCounts.digest_stale} testid="chip-digest-stale">
                <MailWarning className="w-3 h-3" /> Digest stale 14d+
              </FilterChip>
              {(attentionCounts.digest_stale || 0) > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
                  onClick={sendDigestToAllStale}
                  disabled={bulkDigestLoading}
                  data-testid="bulk-send-digest-stale"
                  title="Force-send daily digest to all bands listed in 'Digest stale 14d+'"
                >
                  <MailWarning className="w-3 h-3 mr-1" />
                  {bulkDigestLoading ? "Sending…" : `Send now to all ${attentionCounts.digest_stale}`}
                </Button>
              )}
              {attention && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setAttention(null)}>
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Bands table */}
        <Card className="glass-card">
          <CardHeader>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="flex items-center gap-2">
                <Building2 className="w-5 h-5 text-primary" />
                Bands
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="outline" size="sm"
                  onClick={() => exportBands("csv")}
                  data-testid="export-bands-csv"
                  title="Export filtered bands as CSV"
                >
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
                <Button
                  variant="outline" size="sm"
                  onClick={() => exportBands("xlsx")}
                  data-testid="export-bands-xlsx"
                  title="Export filtered bands as Excel"
                >
                  <Download className="w-4 h-4 mr-1" /> Excel
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHeader label="Band Name" field="name" sort={sort} order={order} onSort={toggleSort} />
                    <SortableHeader label="Status" field="subscription_status" sort={sort} order={order} onSort={toggleSort} />
                    <TableHead>Plan</TableHead>
                    <TableHead>Members</TableHead>
                    <TableHead>Events</TableHead>
                    <TableHead>Last activity</TableHead>
                    <SortableHeader label="Created" field="created_at" sort={sort} order={order} onSort={toggleSort} />
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {bands.length === 0 && !bandsLoading && (
                    <TableRow><TableCell colSpan={8} className="text-center text-muted-foreground py-8">No bands match.</TableCell></TableRow>
                  )}
                  {bands.map((band) => {
                    const lastAct = band.last_activity_at;
                    let actLabel = "—";
                    let actStale = false;
                    if (lastAct) {
                      try {
                        const dt = new Date(lastAct);
                        const days = (Date.now() - dt.getTime()) / 86400000;
                        actStale = days > 30;
                        actLabel = formatDistanceToNow(dt, { addSuffix: true });
                      } catch { actLabel = "—"; }
                    }
                    return (
                    <TableRow key={band.id} data-testid={`band-row-${band.id}`}>
                      <TableCell className="font-medium">
                        <button
                          onClick={() => openDrawer(band)}
                          className="text-left hover:text-primary underline-offset-2 hover:underline"
                          data-testid={`band-name-link-${band.id}`}
                        >
                          {band.name}
                        </button>
                      </TableCell>
                      <TableCell>{getStatusBadge(band.subscription_status)}</TableCell>
                      <TableCell>{band.subscription_plan ? <Badge variant="outline">{band.subscription_plan}</Badge> : "-"}</TableCell>
                      <TableCell>{band.member_count || 0}</TableCell>
                      <TableCell>{band.event_count || 0}</TableCell>
                      <TableCell className={`text-xs ${actStale ? "text-red-400" : "text-muted-foreground"}`} data-testid={`last-activity-${band.id}`}>
                        {actLabel}
                      </TableCell>
                      <TableCell>{band.created_at ? format(new Date(band.created_at), "MMM d, yyyy") : "-"}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="outline" size="sm" onClick={() => openDrawer(band)} title="View band detail" data-testid={`view-band-btn-${band.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleEditSubscription(band)} title="Edit subscription" data-testid={`edit-band-btn-${band.id}`}>
                          <Pencil className="w-4 h-4" />
                        </Button>
                        {band.subscription_status !== "active" && (
                          <Button variant="outline" size="sm" className="text-green-400 border-green-500/30" onClick={() => handleMarkPaid(band.id)} title="Mark paid for 1 year">
                            <CreditCard className="w-4 h-4" />
                          </Button>
                        )}
                        {/* iter-119 — Extend trial */}
                        <Button
                          variant="outline" size="sm"
                          className="text-orange-400 border-orange-500/30"
                          onClick={() => handleExtendTrial(band.id, band.name)}
                          title='Extend trial / set active'
                          data-testid={`extend-trial-btn-${band.id}`}
                        >
                          <Clock className="w-4 h-4" />
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-400 border-red-500/30" onClick={() => openDelete(band)} title="Delete band" data-testid={`delete-band-btn-${band.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            {/* Pagination */}
            <div className="flex items-center justify-between flex-wrap gap-2 mt-4 pt-3 border-t border-white/5">
              <div className="text-xs text-muted-foreground">
                Page {page} of {totalPages} · {total.toLocaleString()} total
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1 || bandsLoading} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="page-prev">
                  <ChevronLeft className="w-4 h-4" /> Prev
                </Button>
                <Input
                  type="number"
                  min={1}
                  max={totalPages}
                  value={page}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    if (!Number.isNaN(v)) setPage(Math.max(1, Math.min(totalPages || 1, v)));
                  }}
                  className="w-16 h-8 text-center"
                  data-testid="page-input"
                />
                <Button variant="outline" size="sm" disabled={page >= totalPages || bandsLoading} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="page-next">
                  Next <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Edit dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Subscription — {selectedBand?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={editForm.subscription_status} onValueChange={(v) => setEditForm({ ...editForm, subscription_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="trial">Trial</SelectItem>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="expired">Expired</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Plan</Label>
                <Select value={editForm.subscription_plan} onValueChange={(v) => setEditForm({ ...editForm, subscription_plan: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly (£5)</SelectItem>
                    <SelectItem value="yearly">Yearly (£50)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Subscription End Date</Label>
                <Input
                  type="date"
                  value={editForm.subscription_end}
                  onChange={(e) => setEditForm({ ...editForm, subscription_end: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Admin Notes</Label>
                <Input
                  value={editForm.notes}
                  onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                  placeholder="Optional notes…"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveSubscription}>Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete dialog — typed confirmation */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="text-red-400 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Delete band
              </DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-3">
              <p>
                You are about to <strong className="text-red-400">permanently delete</strong>{" "}
                <strong>{bandToDelete?.name}</strong> and every player, event, message and file in
                it. This cannot be undone.
              </p>
              <div className="space-y-2">
                <Label className="text-sm">
                  Type the band name (<code className="text-primary">{bandToDelete?.name}</code>) to confirm:
                </Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={bandToDelete?.name || ""}
                  data-testid="delete-band-confirm-input"
                  autoComplete="off"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
              <Button
                variant="destructive"
                onClick={handleDeleteBand}
                disabled={
                  !bandToDelete ||
                  deleteConfirmText.trim().toLowerCase() !==
                    (bandToDelete.name || "").trim().toLowerCase()
                }
                data-testid="delete-band-confirm-btn"
              >
                <Trash2 className="w-4 h-4 mr-2" /> Delete band permanently
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Audit log dialog */}
        <Dialog open={auditOpen} onOpenChange={setAuditOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileClock className="w-5 h-5 text-primary" /> System-admin audit log
              </DialogTitle>
            </DialogHeader>
            <div className="max-h-[60vh] overflow-y-auto -mx-2 px-2">
              {auditLoading ? (
                <p className="text-muted-foreground py-8 text-center">Loading…</p>
              ) : auditEntries.length === 0 ? (
                <p className="text-muted-foreground py-8 text-center">No entries yet.</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>Actor</TableHead>
                      <TableHead>IP</TableHead>
                      <TableHead>Target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {auditEntries.map((e) => (
                      <TableRow key={e.id} data-testid={`audit-row-${e.id}`}>
                        <TableCell className="text-xs">{fmtAuditTime(e.created_at)}</TableCell>
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={
                              e.action === "login_failed" || e.action === "lockout_triggered"
                                ? "text-red-300 border-red-500/40"
                                : e.action === "band_deleted"
                                ? "text-orange-300 border-orange-500/40"
                                : "text-muted-foreground"
                            }
                          >
                            {e.action}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{e.actor || "—"}</TableCell>
                        <TableCell className="text-xs"><code>{e.ip || "—"}</code></TableCell>
                        <TableCell className="text-xs">{e.target_band_name || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAuditOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Band Detail Drawer */}
        <BandDetailDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          bandId={drawerBandId}
          api={systemApi}
          onActionDone={fetchAll}
        />

        {/* Broadcast composer dialog (iter-103) */}
        <Dialog open={broadcastOpen} onOpenChange={setBroadcastOpen}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-primary" /> Send broadcast email
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label>Subject</Label>
                <Input
                  value={broadcastSubject}
                  onChange={(e) => setBroadcastSubject(e.target.value)}
                  placeholder="e.g. New feature: PDF exports now available"
                  data-testid="broadcast-subject"
                />
              </div>
              <div className="space-y-2">
                <Label>Body (HTML supported)</Label>
                <textarea
                  value={broadcastBody}
                  onChange={(e) => setBroadcastBody(e.target.value)}
                  placeholder="<p>Hi there,</p><p>...</p>"
                  rows={10}
                  className="w-full font-mono text-xs rounded-md border border-input bg-background px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary/40"
                  data-testid="broadcast-body"
                />
                <p className="text-xs text-muted-foreground">
                  Wrapped in a 600px container with a footer disclaimer.
                </p>
              </div>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={broadcastTestOnly}
                  onChange={(e) => setBroadcastTestOnly(e.target.checked)}
                  data-testid="broadcast-test-only"
                />
                <span>Test mode (send only to my support email)</span>
              </label>
              {!broadcastTestOnly && (
                <div className="rounded-md border border-orange-500/40 bg-orange-500/10 text-orange-200 text-xs px-3 py-2">
                  <AlertTriangle className="w-3 h-3 inline mr-1" />
                  Live mode — will email every band admin in the database.
                </div>
              )}

              {broadcastHistory.length > 0 && (
                <div className="space-y-1 pt-3 border-t border-white/5">
                  <Label className="text-xs text-muted-foreground">Recent broadcasts</Label>
                  <div className="max-h-40 overflow-y-auto space-y-1 text-xs">
                    {broadcastHistory.map((b) => (
                      <div key={b.id} className="flex items-center gap-2 py-1">
                        <Badge variant="outline" className={b.test_only ? "text-blue-300 border-blue-500/30" : "text-emerald-300 border-emerald-500/30"}>
                          {b.test_only ? "TEST" : "LIVE"}
                        </Badge>
                        <span className="flex-1 truncate">{b.subject}</span>
                        <span className="text-muted-foreground">{b.sent_count}/{b.recipient_count}</span>
                        <span className="text-muted-foreground">{format(new Date(b.created_at), "MMM d HH:mm")}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBroadcastOpen(false)} disabled={broadcastSending}>Cancel</Button>
              <Button
                onClick={sendBroadcast}
                disabled={broadcastSending}
                data-testid="broadcast-send-btn"
                className={broadcastTestOnly ? "" : "bg-orange-500 hover:bg-orange-600 text-white"}
              >
                {broadcastSending ? "Sending…" : (broadcastTestOnly ? "Send test" : "Send to all admins")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

// ---- Small presentational helpers ----
function FilterChip({ active, onClick, count, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border transition-colors ${
        active
          ? "bg-primary/20 border-primary/50 text-primary"
          : "bg-background/40 border-white/10 text-muted-foreground hover:border-white/30"
      }`}
    >
      {children}
      {typeof count === "number" && (
        <span className={`ml-1 px-1.5 rounded-full text-[10px] ${active ? "bg-primary/30" : "bg-white/10"}`}>
          {count}
        </span>
      )}
    </button>
  );
}

function SortableHeader({ label, field, sort, order, onSort }) {
  const active = sort === field;
  return (
    <TableHead>
      <button
        type="button"
        onClick={() => onSort(field)}
        className={`inline-flex items-center gap-1 text-left hover:text-primary ${active ? "text-primary" : ""}`}
        data-testid={`sort-${field}`}
      >
        {label}
        <ArrowUpDown className={`w-3 h-3 ${active ? "opacity-100" : "opacity-30"}`} />
        {active && <span className="text-[10px]">{order === "asc" ? "↑" : "↓"}</span>}
      </button>
    </TableHead>
  );
}

// iter-106 — system-health pill (ok = emerald, warn = amber, error = red).
function HealthPill({ ok, label, warnIfNotOk = false, testid }) {
  let cls = "border-emerald-500/40 bg-emerald-500/10 text-emerald-300";
  if (!ok) {
    cls = warnIfNotOk
      ? "border-amber-500/40 bg-amber-500/10 text-amber-300"
      : "border-red-500/40 bg-red-500/10 text-red-300";
  }
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] border ${cls}`}
      data-testid={testid}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-current opacity-70" />
      {label}
    </div>
  );
}

export default SystemAdmin;
